export const AMOUNT_OUTSIDE_LIMITS_REASON_CODES = {
  BELOW_MIN_AMOUNT: "BELOW_MIN_AMOUNT",
  ABOVE_MAX_AMOUNT: "ABOVE_MAX_AMOUNT",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
};

/**
 * getIfAmountIsWithinLimitsForPM
 * @description Takes in a single PM and a 'capability' (purchase, payout, etc)
 * txnLimits payload, and some amount.
 * If we find a max/min the pm's provider and processing_currency we check the passed
 * in value to see if it's within limits or not.
 * If the amount is outside the limits, we'll hand back a reason code (more than max, less than min, etc)
 */
export const getIfAmountIsWithinLimitsForPM = ({ pm, txnLimits, amountToCheck }) => {
  try {
    const min = (txnLimits || {})[pm.provider]?.[pm.processing_currency || "USD"]?.min || null;
    const max = (txnLimits || {})[pm.provider]?.[pm.processing_currency || "USD"]?.max || null;
    const minAmountValid = min === null || !amountToCheck || amountToCheck >= min;
    const maxAmountValid = max === null || !amountToCheck || amountToCheck <= max;
    let reasonCode = null; // default value
    if (!minAmountValid) reasonCode = AMOUNT_OUTSIDE_LIMITS_REASON_CODES.BELOW_MIN_AMOUNT;
    if (!maxAmountValid) reasonCode = AMOUNT_OUTSIDE_LIMITS_REASON_CODES.ABOVE_MAX_AMOUNT;
    return { amountIsWithinLimits: minAmountValid && maxAmountValid, reasonCode, min, max };
  } catch (_err) {
    // we shouldn't really fail here (with a valid amount) as issues with any passed in values should get sorted with defaults, but just incase let's allow it to pass through here
    return { amountIsWithinLimits: true, reasonCode: null, min: null, max: null };
  }
};

/**
 * getIfAmountCanBeCoveredByPMs
 * @description Checks amount passed in against all payment methods
 * (including wallet and it's balance) and their txnLimits (if available).
 * If no transaction limits are found, we assume there is no limit and
 * can be good for any amount.
 */
export const getIfAmountCanBeCoveredByPMs = (pms, amount, txnLimits, ignoreWallets = false) => {
  if (pms && pms.length === 0) return false; // if no payment methods, then we're treating it as if the user does not have enough to cover anything (even 0).  With this setup, we can prompt the user to add a PM
  if (!pms || !amount || !txnLimits) return true; // default to true if missing required info or amount is 0
  let remainingAmount = amount; // keep track of what we need to cover

  if (!ignoreWallets) {
    // check wallets
    const wallets = pms.filter((pm) => pm.provider === "wallet");
    wallets.forEach((pm) => {
      let availableInWallet = pm.provider_data.balance || 0;
      const maxLimitForWallet =
        (txnLimits || {})[pm.provider]?.[pm.processing_currency || "USD"]?.max || null;
      if (maxLimitForWallet !== null) {
        availableInWallet = Math.min(availableInWallet, maxLimitForWallet); // whichever is less, amount available or max limit
      }
      remainingAmount = remainingAmount - availableInWallet;
    });
    if (remainingAmount <= 0) {
      // wallet has enough to cover so let's just return here
      return true;
    }
  }

  // search non-wallets, and see what is the max amount that can be supported
  const nonWallets = pms.filter((pm) => pm.provider !== "wallet");
  let onePMFoundWithNoMaxLimit = false; // if we find one with no max, then we're all set
  let maxMaxLimit = 0; // holds the value of the maximum max limit of all the PMs (ignores ones with no limit as the var above takes care of that)

  for (let i = 0; i < nonWallets.length; i++) {
    const pm = nonWallets[i];
    const max = (txnLimits || {})[pm.provider]?.[pm.processing_currency || "USD"]?.max || null;
    if (max === null) {
      onePMFoundWithNoMaxLimit = true;
      break; // this pm has no limit, so no need to keep going
    } else {
      maxMaxLimit = max > maxMaxLimit ? max : maxMaxLimit;
    }
  }

  if (onePMFoundWithNoMaxLimit) {
    return true;
  } else {
    remainingAmount = remainingAmount - maxMaxLimit;
    const enoughtToCover = remainingAmount <= 0;
    return enoughtToCover;
  }
};
